/**
 * The widget remembers the salon's region (localStorage "widget_region",
 * per w<id>.boona.app origin) and opens the next visit's session straight on
 * https://api-<region>.boona.app. That memory can be wrong: the salon moved
 * shards, or an earlier visit was routed by the old country rule (a Russian
 * salon on SE was sent to BY until 2026-09-24). The wrong shard doesn't know
 * the host and answers 404 "Unknown widget host" — and the widget stayed
 * broken for that visitor for good.
 *
 * So: a session that fails on a REMEMBERED region is retried once on the
 * default API (boona.app), which knows every salon and returns the right
 * region. A license_expired refusal is an answer, not a routing failure —
 * it is never retried.
 */
export async function withRegionFallback<T>(opts: {
  cachedRegion: string | null;
  defaultBase: string;
  configure: (base: string) => void;
  forget: () => void;
  create: () => Promise<T>;
  isFinal: (e: unknown) => boolean;
}): Promise<T> {
  const { cachedRegion, defaultBase, configure, forget, create, isFinal } = opts;
  const remembered = Boolean(cachedRegion && cachedRegion !== "default");
  configure(remembered ? `https://api-${cachedRegion}.boona.app` : defaultBase);
  try {
    return await create();
  } catch (e) {
    if (!remembered || isFinal(e)) throw e;
    forget();
    configure(defaultBase);
    return await create();
  }
}
